import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useElementHeight } from 'visitor-ui-component-library/utils/hooks/useElementHeight';
import styled from 'styled-components';
import { WHITE, DEFAULT_INPUT_BORDER_COLOR, DEFAULT_INPUT_BORDER_COLOR_DISABLED } from 'visitor-ui-component-library/theme/ColorConstants';
import { COMPOSER_INLINE_MARGIN, COMPOSER_BOTTOM_MARGIN } from '../widget-dimensions/constants/dimensions';
import { useWidgetResizeContext } from '../contexts/WidgetResizeContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SLIDE_UP_ANIMATION_DURATION = 0.5;
const FADE_IN_ANIMATION_DURATION = 1;
export const ANIMATION_DELAY = 1000;
const ComposerWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ComposerWrapper"
})(["position:relative;min-height:", "px;"], ({
  $composerHeight
}) => $composerHeight + COMPOSER_BOTTOM_MARGIN);
const Composer = styled.div.withConfig({
  displayName: "MessageComposer__Composer"
})(["max-width:100%;background-color:", ";flex-shrink:0;transition:", ";margin:0 ", "px ", "px;border:1px solid ", ";border-radius:28px;padding:0 8px;[data-test-id='widget-textarea']{min-height:38px !important;}"], WHITE, ({
  $isResizing
}) => $isResizing ? 'none' : `margin ${SLIDE_UP_ANIMATION_DURATION}s linear`, COMPOSER_INLINE_MARGIN, ({
  $showDisclaimer,
  $disclaimerHeight
}) => $showDisclaimer && $disclaimerHeight > 0 ? $disclaimerHeight + 5 : COMPOSER_BOTTOM_MARGIN, ({
  $disabled
}) => $disabled ? DEFAULT_INPUT_BORDER_COLOR_DISABLED : DEFAULT_INPUT_BORDER_COLOR);
const Inner = styled.div.withConfig({
  displayName: "MessageComposer__Inner"
})(["display:flex;width:100%;align-items:center;justify-content:space-between;flex-wrap:nowrap;gap:4px;"]);
const InputWrapper = styled.div.withConfig({
  displayName: "MessageComposer__InputWrapper"
})(["flex:1 1 0px;"]);
const ActionsWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ActionsWrapper"
})(["display:flex;flex:0 0 auto;", ""], ({
  children
}) => !children && 'display: none;');
const Disclaimer = styled.div.withConfig({
  displayName: "MessageComposer__Disclaimer"
})(["font-size:0.9em;text-align:center;opacity:", ";width:100%;transition:opacity ", "s ease-in;position:absolute;padding:0 20px;bottom:3px;"], ({
  $isVisible
}) => $isVisible ? '1' : '0', FADE_IN_ANIMATION_DURATION);
export default function MessageComposer({
  FileUploads = null,
  inputToRender,
  children,
  disabled,
  disclaimerText
}) {
  const {
    isResizing
  } = useWidgetResizeContext();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const disclaimerRef = useRef(null);
  const composerRef = useRef(null);
  // TODO: remove cast when on React 19 types (HubSpotEngineering/fefw#73)
  const composerHeight = useElementHeight(composerRef);
  const [disclaimerHeight, setDisclaimerHeight] = useState(0);
  useLayoutEffect(() => {
    const el = disclaimerRef.current;
    if (!el) {
      setDisclaimerHeight(0);
      return;
    }
    const updateHeight = () => setDisclaimerHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [disclaimerText]);
  useEffect(() => {
    if (disclaimerText) {
      const timer = setTimeout(() => {
        setShowDisclaimer(true);
      }, ANIMATION_DELAY);
      return () => clearTimeout(timer);
    }
  }, [disclaimerText]);
  return /*#__PURE__*/_jsxs(ComposerWrapper, {
    showDisclaimer: showDisclaimer,
    $disclaimerHeight: disclaimerHeight,
    $composerHeight: composerHeight,
    "data-test-id": "composer-wrapper",
    children: [/*#__PURE__*/_jsxs(Composer, {
      $disabled: disabled,
      $showDisclaimer: showDisclaimer,
      $disclaimerHeight: disclaimerHeight,
      $isResizing: isResizing,
      ref: composerRef,
      children: [FileUploads, /*#__PURE__*/_jsxs(Inner, {
        children: [/*#__PURE__*/_jsx(InputWrapper, {
          children: inputToRender
        }), /*#__PURE__*/_jsx(ActionsWrapper, {
          children: children
        })]
      })]
    }), disclaimerText && /*#__PURE__*/_jsx(Disclaimer, Object.assign({
      ref: disclaimerRef,
      $isVisible: showDisclaimer
    }, showDisclaimer && {
      'aria-label': disclaimerText
    }, {
      children: disclaimerText
    }))]
  });
}
MessageComposer.displayName = 'MessageComposer';